import type { AppIconInput } from "./appIcon";
import { isIosInCallService } from "./iosInCallApp";

/**
 * Shape returned by convex/appStoreMetadata.getByBundleId (and the resolve
 * action). Mirrors the cache document minus Convex system fields.
 */
export type AppStoreMetadata = {
  bundleId: string;
  trackName?: string;
  /** Signed Convex storage URL, or a legacy iTunes artwork URL during migration. */
  iconUrl: string | null;
  trackId?: number;
  country: string;
  lookupCountriesTried?: string[];
  resolvedAt: number;
  lookupStatus: "found" | "not_found";
};

const REVERSE_DOMAIN_PREFIX = /^(com|org|net|io|edu|gov|app|dev|me|co|uk|de|fr|it)\./i;
const WEB_DOMAIN_SUFFIX = /\.(com|org|net|io|edu|gov|app|dev|me|co|uk|de|fr|it)$/i;
const PACKAGE_SEGMENT = /^[a-zA-Z][a-zA-Z0-9_-]*$/;

/** Lowercase cache key. iTunes bundleId lookup is case-insensitive. */
export function normalizeBundleIdKey(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * True only for reverse-domain iOS-style bundle identifiers. Rejects empty
 * strings, plain words, and web domains like "google.com" (which are
 * package-shaped but are not bundle IDs). Keep in sync with the predicate in
 * convex/appStoreMetadata.ts.
 */
export function isIosBundleId(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed || !trimmed.includes(".")) return false;
  if (WEB_DOMAIN_SUFFIX.test(trimmed) && !REVERSE_DOMAIN_PREFIX.test(trimmed)) {
    return false;
  }
  if (REVERSE_DOMAIN_PREFIX.test(trimmed)) return true;
  const segments = trimmed.split(".");
  return segments.length >= 2 && segments.every((segment) => PACKAGE_SEGMENT.test(segment));
}

/**
 * Returns the normalized (lowercase) App Store cache key for an app row, or null
 * when the row is not eligible for an App Store lookup. Skips websites and reads
 * either appIdentifier or displayName because iOS Screen Time sometimes sends
 * the bundle ID as the displayName (e.g. displayName === "com.amazon.aiv.AIVApp").
 */
export function appStoreLookupBundleId({
  displayName,
  appIdentifier,
  category,
}: AppIconInput): string | null {
  if ((category ?? "").trim().toLowerCase() === "website") return null;
  if (isIosInCallService(appIdentifier, displayName)) return null;

  const id = (appIdentifier ?? "").trim();
  if (isIosBundleId(id)) return normalizeBundleIdKey(id);

  const display = (displayName ?? "").trim();
  if (isIosBundleId(display)) return normalizeBundleIdKey(display);

  return null;
}

/**
 * Shortens verbose App Store trackName values for UI display only. Applies colon
 * truncation first, then spaced-hyphen truncation. Does not split on lone `-`.
 */
export function normalizeAppStoreDisplayName(raw: string): string {
  let name = raw;

  const colonIndex = name.indexOf(":");
  if (colonIndex >= 0) {
    name = name.slice(0, colonIndex).trim();
  }

  const spacedHyphenIndex = name.indexOf(" - ");
  if (spacedHyphenIndex >= 0) {
    name = name.slice(0, spacedHyphenIndex).trim();
  }

  return name.trim();
}

/** Found metadata with a usable App Store display name, else null. */
export function appStoreTrackName(metadata: AppStoreMetadata | null | undefined): string | null {
  if (!metadata || metadata.lookupStatus !== "found") return null;
  const raw = metadata.trackName;
  if (raw == null || !raw.trim()) return null;
  const normalized = normalizeAppStoreDisplayName(raw);
  return normalized || null;
}

/**
 * Found metadata with a usable icon URL (Convex signed storage URL, or a legacy
 * iTunes artwork URL served as a migration bridge until backfill completes).
 */
export function appStoreIconUrl(metadata: AppStoreMetadata | null | undefined): string | null {
  if (!metadata || metadata.lookupStatus !== "found") return null;
  const url = metadata.iconUrl?.trim();
  return url ? url : null;
}
